<template>
  <div class="content-text mt-3">
    <h2 class="title-2 text-left">3 Componente de Vínculo e acompanhamento</h2>

    <p>
      Esse componente incentiva a vinculação das pessoas à sua equipe de
      referência e a realização do cuidado a essa população. Não basta somente
      cadastrar, é preciso entender quais são as pessoas do território que fazem
      uso dos serviços ofertados na Unidade Básica de Saúde (UBS), além de
      estimular a equipe a visitar as famílias e, dessa forma, fortalecer a
      Estratégia de Saúde da Família (ESF).
    </p>

    <p>
      Segundo o IBGE, o município de {{ dados.etapa1.cidade.no_municipio }} –
      {{ dados.etapa1.estado.uf }}, possui {{ populacao }} habitantes, porte
      populacional ({{ portePopulacionalTexto }}). O Quadro 2 apresenta a
      quantidade mínima e máxima de pessoas recomendadas por equipe, assim como
      a quantidade de pessoas no SISAB até dezembro de 2023. É fundamental
      equilibrar o número mínimo e máximo de pessoas por equipe. Para alcançar
      essa equidade, é possível redistribuir os indivíduos entre as equipes já
      existentes por meio de remapeamento e territorialização, ou até mesmo
      implantar novas equipes, caso o e-GESTOR apresente teto para credenciar
      mais equipes.
    </p>

    <div class="container-table">
      <span style="text-transform: uppercase; font-weight: bold"
        >Quadro 2 - Quantidade mínima e máxima de pessoas recomendada por
        equipe</span
      >
      <table>
        <thead>
          <tr>
            <th colspan="2">Pessoas por eSF 40h</th>
            <th colspan="2">Pessoas por eAP 30h</th>
            <th colspan="2">Pessoas por eAP 20h</th>
          </tr>
          <tr>
            <th>Recomendado</th>
            <th>Máxima</th>
            <th>Recomendado</th>
            <th>Máxima</th>
            <th>Recomendado</th>
            <th>Máxima</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(item, index) in parametrosCadastroFiltrados" :key="index">
            <td class="text-center">{{ item.esf.parametro }}</td>
            <td class="text-center">{{ item.esf.maximo }}</td>
            <td class="text-center">{{ item.eap30.parametro }}</td>
            <td class="text-center">{{ item.eap30.maximo }}</td>
            <td class="text-center">{{ item.eap20.parametro }}</td>
            <td class="text-center">{{ item.eap20.maximo }}</td>
          </tr>
        </tbody>
      </table>

      <span class="fonte"><strong>Fonte:</strong> Brasil, 2024.</span>
    </div>

    <p class="mt-4">
      Vale salientar que o atendimento não pode ser negado na UBS. As pessoas
      beneficiárias do Programa Bolsa Família (PBF), Benefício de Prestação
      Continuada (BPC), os idosos com 60 anos ou mais e as crianças de até 5
      anos de idade são priorizadas no CVAT.
    </p>

    <p>
      O Quadro 3 apresenta a quantidade por tipo de equipe, os valores por
      equipe e o total mensal a ser recebido neste componente.
    </p>

    <div class="container-table">
      <span style="text-transform: uppercase; font-weight: bold"
        >Quadro 3 - Valores por tipo de equipe no CVAT</span
      >
      <table>
        <thead>
          <tr>
            <th>QUANTIDADE</th>
            <th>CLASSIFICAÇÃO</th>
            <th>VALOR POR EQUIPE</th>
            <th>TOTAL MENSAL</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in equipes.filter(
              (el) => Number(el.quantidade || 0) > 0
            )"
            :key="index"
          >
            <td class="text-center">{{ item.quantidade }} {{ item.label }}</td>
            <td class="text-center">{{ item.classificacao }}</td>
            <td class="text-center">R$ {{ item.valorPorEquipe }}</td>
            <td class="text-center">R$ {{ item.valorTotalPorEquipe }}</td>
          </tr>
          <tr class="grey">
            <td colspan="3"><strong>Total geral</strong></td>
            <td class="text-center">
              <strong>
                R$
                {{
                  totalGeral.toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                  })
                }}
              </strong>
            </td>
          </tr>
        </tbody>
      </table>
      <span class="fonte" v-html="fonte"></span>
    </div>

    <p class="mt-3">
      Cada equipe pode ser classificada no CVAT como:
      <b>REGULAR, SUFICIENTE, BOM e ÓTIMO</b>. A classificação ÓTIMA é a maior
      nota que as equipes do município podem receber. A implantação imediata do
      Monitoramento e Avaliação na APS (M & A na APS) torna mais fácil atingir o
      <b>"ÓTIMO"</b> como desempenho máximo municipal.
    </p>
  </div>
</template>

<script>
import { getLabelRegra } from '../utils.js'

export default {
  props: {
    dados: {
      type: Object,
      default: () => {},
    },
    regras: {
      type: Array,
      default: () => {},
    },
    fonte: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      populacao: 0,
      ubs: [],
      equipes: [],
      totalGeral: 0,
      parametrosCadastro: [
        {
          porte: '1 - Até 20 mil',
          esf: { parametro: 2000, maximo: 3000 },
          eap30: { parametro: 1500, maximo: 2250 },
          eap20: { parametro: 1000, maximo: 1500 },
          id: 1,
        },
        {
          porte: '2 - Acima de 20 mil até 50 mil',
          esf: { parametro: 2500, maximo: 3750 },
          eap30: { parametro: 1875, maximo: 2813 },
          eap20: { parametro: 1250, maximo: 1875 },
          id: 2,
        },
        {
          porte: '3 - Acima de 50 mil até 100 mil',
          esf: { parametro: 2750, maximo: 4125 },
          eap30: { parametro: 2063, maximo: 3095 },
          eap20: { parametro: 1375, maximo: 2063 },
          id: 3,
        },
        {
          porte: '4 - Acima de 100 mil',
          esf: { parametro: 3000, maximo: 4500 },
          eap30: { parametro: 2250, maximo: 3375 },
          eap20: { parametro: 1500, maximo: 2250 },
          id: 4,
        },
      ],
    }
  },
  mounted() {
    this.buscarMunicipio()
    this.formatarEquipes()
  },
  computed: {
    parametrosCadastroFiltrados() {
      const porte = this.dados?.etapa3?.porte_populacional?.value

      return this.parametrosCadastro.filter((item, index) => {
        return Number(porte) === item.id
      })
    },

    /**
     * Retorna o texto do porte populacional.
     *
     * @returns {String}
     */
    portePopulacionalTexto() {
      const porte = Number(this.dados?.etapa3?.porte_populacional?.value)

      const item = this.parametrosCadastro.find((item) => {
        return item.id === porte
      })

      if (!item) {
        return ''
      }

      return item.porte
    },
  },
  methods: {
    /**
     * Formata as equipes do componente vínculo e acompanhamento.
     *
     * @returns {void}
     */
    formatarEquipes() {
      const equipes = this.dados?.etapa4?.equipes || []

      this.equipes = []
      this.totalGeral = 0

      equipes
        .filter((item) => item.componente === 'CVAT')
        .forEach((item) => {
          const regra = this.regras.find((regra) => {
            return regra.label === getLabelRegra(item.equipe)
          })

          if (!regra) {
            return
          }

          const classificacao = regra.classificacao.find((classificacao) => {
            return classificacao.tipo === item.classificacaoLabel
          })

          if (!classificacao) {
            return
          }

          const totalPorEquipe =
            classificacao.valor * Number(item.quantidade || 0)

          this.equipes.push({
            label: regra.label,
            quantidade: item.quantidade,
            classificacao: item.classificacaoLabel.toUpperCase(),
            valorPorEquipe: classificacao.valor.toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
            }),
            valorTotalPorEquipe: totalPorEquipe.toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
            }),
          })

          this.totalGeral += totalPorEquipe
        })
    },

    async buscarMunicipio() {
      try {
        const response = await this.$apiPublic.get(
          `/cofinanciamento-2024/populacao/${this.dados.etapa1.cidade.co_ibge}`
        )
        this.populacao = response.data.populacaoIBGE
      } catch (error) {
        console.log(error)
      }
    },

    async buscarUbsDoMunicipio() {
      try {
        const response = await this.$apiPublic.get(
          `/cofinanciamento-2024/ubs/${this.dados.etapa1.cidade.co_ibge}`
        )
        this.ubs = response.data
      } catch (error) {
        console.log(error)
      }
    },
  },
}
</script>

<style lang="scss"></style>
